<script setup lang="ts">
  defineOptions({
    name: 'CPPortalSidebarNav',
  })

  import type { PropType } from 'vue'

  import { computed, h, onMounted, onUnmounted, reactive, watch } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { createElementId } from '@common/core-layer/utils'

  import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'

  import AppSidebarNav from '@common/shared-ui-layer/components/AppSidebar/AppSidebarNav.vue'

  import AppSidebarNavItem from '@common/shared-ui-layer/components/AppSidebar/AppSidebarNavItem.vue'

  import { useCoursesAdminPreview } from '@courses/admin-preview-layer/composables/useCoursesAdminPreview'

  import CPText from '@ui/components/CPText.vue'

  import CPTooltip from '@ui/components/CPTooltip.vue'

  import type { AppSidebarNavGroup } from '@common/shared-ui-layer/types/app-sidebar-nav'

  import CPPortalSidebarIcon from './CPPortalSidebarIcon.vue'

  import type { PortalNavigationGroup, PortalNavigationItem } from '../types/portal-sidebar'

  const props = defineProps({
    portalName: {
      type: String,
      required: true,
    },
    navigationGroups: {
      type: Array as PropType<PortalNavigationGroup[]>,
      required: true,
    },
    selectedItem: {
      type: Object as PropType<PortalNavigationItem | undefined>,
      default: undefined,
    },
    isCollapsed: {
      type: Boolean,
      required: true,
    },
  })

  const emit = defineEmits<{
    itemClick: [item: PortalNavigationItem]
  }>()

  const COLLAPSED_TOOLTIP_HIDE_DELAY_MS = 150
  const COLLAPSED_TOOLTIP_OFFSET_PX = 6

  const { t } = useI18n()

  const { getRoutePath } = useCPNavigation()

  const { isActive, builderMode } = useCoursesAdminPreview()

  const isAdminPreviewBuilder = computed(() => Boolean(isActive.value && builderMode.value))

  const sidebarNavGroups = computed((): AppSidebarNavGroup[] =>
    props.navigationGroups.map(group => ({
      id: group.groupId,
      label: group.labelKey ? t(group.labelKey) : undefined,
      items: group.items.map(item => ({
        id: item.id,
        label: item.name,
        to: getRoutePath({ name: item.routeName }),
        meta: { portalItem: item },
      })),
    }))
  )

  const activeItemId = computed(() => props.selectedItem?.id)

  const collapsedTooltipInteractions = reactive<
    Record<string, { hovered: boolean; focused: boolean; contentHovered: boolean; dismissedUntilPointerLeave: boolean }>
  >({})

  const collapsedTooltipHideTimers = new Map<string, ReturnType<typeof setTimeout>>()

  let hasDocumentEscapeListener = false

  const getCollapsedTooltipInteraction = (itemId: string) => {
    if (!collapsedTooltipInteractions[itemId]) {
      collapsedTooltipInteractions[itemId] = {
        hovered: false,
        focused: false,
        contentHovered: false,
        dismissedUntilPointerLeave: false,
      }
    }

    return collapsedTooltipInteractions[itemId]
  }

  const clearCollapsedTooltipHideTimer = (itemId: string) => {
    const timer = collapsedTooltipHideTimers.get(itemId)
    if (timer) {
      clearTimeout(timer)
      collapsedTooltipHideTimers.delete(itemId)
    }
  }

  const scheduleCollapsedTooltipHide = (itemId: string, hide: () => void) => {
    clearCollapsedTooltipHideTimer(itemId)
    collapsedTooltipHideTimers.set(
      itemId,
      setTimeout(() => {
        hide()
        collapsedTooltipHideTimers.delete(itemId)
      }, COLLAPSED_TOOLTIP_HIDE_DELAY_MS)
    )
  }

  const isCollapsedTooltipVisible = (itemId: string) => {
    const interaction = collapsedTooltipInteractions[itemId]
    return Boolean(interaction && (interaction.hovered || interaction.focused || interaction.contentHovered))
  }

  const dismissCollapsedTooltip = (itemId: string, options: { suppressUntilPointerLeave?: boolean } = {}) => {
    clearCollapsedTooltipHideTimer(itemId)
    const interaction = collapsedTooltipInteractions[itemId]
    if (!interaction) return

    const wasPointerOnTrigger = interaction.hovered
    interaction.hovered = false
    interaction.focused = false
    interaction.contentHovered = false
    interaction.dismissedUntilPointerLeave = Boolean(options.suppressUntilPointerLeave && wasPointerOnTrigger)
  }

  const dismissOtherCollapsedTooltips = (itemId: string) => {
    Object.keys(collapsedTooltipInteractions).forEach(id => {
      if (id !== itemId) dismissCollapsedTooltip(id)
    })
  }

  const dismissAllCollapsedTooltips = () => {
    Object.keys(collapsedTooltipInteractions).forEach(id => dismissCollapsedTooltip(id))
  }

  const dismissVisibleCollapsedTooltips = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !props.isCollapsed) return

    const visibleItemIds = Object.keys(collapsedTooltipInteractions).filter(isCollapsedTooltipVisible)
    if (!visibleItemIds.length) return

    visibleItemIds.forEach(id => dismissCollapsedTooltip(id, { suppressUntilPointerLeave: true }))
  }

  const setDocumentEscapeListener = (enabled: boolean) => {
    if (enabled && !hasDocumentEscapeListener) {
      document.addEventListener('keydown', dismissVisibleCollapsedTooltips)
      hasDocumentEscapeListener = true
    } else if (!enabled && hasDocumentEscapeListener) {
      document.removeEventListener('keydown', dismissVisibleCollapsedTooltips)
      hasDocumentEscapeListener = false
    }
  }

  const isPortalNavigationItem = (value: unknown): value is PortalNavigationItem => {
    if (!value || typeof value !== 'object') return false

    const item = value as Partial<PortalNavigationItem>
    return typeof item.id === 'string' && typeof item.name === 'string' && typeof item.routeName === 'string'
  }

  const getPortalItem = (item: { meta?: Record<string, unknown> }) => {
    const portalItem = item.meta?.portalItem
    return isPortalNavigationItem(portalItem) ? portalItem : undefined
  }

  const handleItemClick = (item: { id: string; meta?: Record<string, unknown> }, _event: MouseEvent) => {
    const portalItem = getPortalItem(item)
    if (portalItem) {
      emit('itemClick', portalItem)
    }
  }

  const renderCollapsedTooltipItem = (
    item: PortalNavigationItem | undefined,
    isActiveItem: boolean,
    emitClick: (event: MouseEvent) => void
  ) => {
    if (!item) return null

    const link = () =>
      h(
        AppSidebarNavItem,
        {
          to: getRoutePath({ name: item.routeName }),
          isActive: isActiveItem,
          linkId: createElementId('cp-portal-sidebar', 'nav', item.id),
          ariaLabel: item.name,
          onFocus: () => {
            dismissOtherCollapsedTooltips(item.id)
            clearCollapsedTooltipHideTimer(item.id)
            getCollapsedTooltipInteraction(item.id).focused = true
          },
          onBlur: () => {
            getCollapsedTooltipInteraction(item.id).focused = false
          },
          onMouseenter: () => {
            const interaction = getCollapsedTooltipInteraction(item.id)
            if (interaction.dismissedUntilPointerLeave) return

            dismissOtherCollapsedTooltips(item.id)
            clearCollapsedTooltipHideTimer(item.id)
            interaction.contentHovered = false
            interaction.hovered = true
          },
          onMouseleave: () => {
            const interaction = getCollapsedTooltipInteraction(item.id)
            interaction.dismissedUntilPointerLeave = false
            scheduleCollapsedTooltipHide(item.id, () => {
              interaction.hovered = false
            })
          },
          onKeydown: (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
              event.preventDefault()
              event.stopPropagation()
              dismissCollapsedTooltip(item.id, { suppressUntilPointerLeave: true })
            }
          },
          onClick: emitClick,
        },
        {
          leading: () =>
            h(CPPortalSidebarIcon, {
              channel: { iconName: item.iconName, fallbackIcon: item.fallbackIcon },
              isActive: isActiveItem,
              class: 'h-4 w-4 flex-shrink-0 text-inherit',
            }),
        }
      )

    return h(
      CPTooltip,
      {
        placement: 'right',
        variant: 'dark',
        trigger: 'manual',
        show: isCollapsedTooltipVisible(item.id),
        to: 'body',
        offset: COLLAPSED_TOOLTIP_OFFSET_PX,
      },
      {
        trigger: link,
        default: () =>
          h(
            'span',
            {
              class: 'cp-portal-sidebar__tooltip-content pointer-events-auto block',
              onMouseenter: () => {
                clearCollapsedTooltipHideTimer(item.id)
                const interaction = getCollapsedTooltipInteraction(item.id)
                interaction.hovered = false
                interaction.contentHovered = true
              },
              onMouseleave: () =>
                scheduleCollapsedTooltipHide(item.id, () => {
                  getCollapsedTooltipInteraction(item.id).contentHovered = false
                }),
            },
            item.name
          ),
      }
    )
  }

  onMounted(() => setDocumentEscapeListener(props.isCollapsed))

  onUnmounted(() => {
    setDocumentEscapeListener(false)
    collapsedTooltipHideTimers.forEach(clearTimeout)
    collapsedTooltipHideTimers.clear()
  })

  watch(
    () => props.isCollapsed,
    isCollapsed => {
      setDocumentEscapeListener(isCollapsed)
      if (!isCollapsed) dismissAllCollapsedTooltips()
    }
  )
</script>

<template>
  <AppSidebarNav
    :aria-label="portalName"
    :groups="sidebarNavGroups"
    :active-item-id="activeItemId"
    :is-collapsed="isCollapsed"
    :inert="isAdminPreviewBuilder"
    group-header-id-prefix="cp-portal-sidebar"
    @item-click="handleItemClick"
  >
    <template #item="{ item, isActive: isActiveItem, emitClick }">
      <component :is="renderCollapsedTooltipItem(getPortalItem(item), isActiveItem, emitClick)" v-if="isCollapsed && getPortalItem(item)" />
      <AppSidebarNavItem
        v-else-if="!isCollapsed && getPortalItem(item)"
        :to="item.to"
        :is-active="isActiveItem"
        :link-id="createElementId('cp-portal-sidebar', 'nav', item.id)"
        @click="emitClick"
      >
        <template #leading>
          <CPPortalSidebarIcon
            :channel="{
              iconName: getPortalItem(item)?.iconName,
              fallbackIcon: getPortalItem(item)?.fallbackIcon,
            }"
            :is-active="isActiveItem"
            class="h-4 w-4 flex-shrink-0 text-inherit"
          />
        </template>
        <CPText size="lg" weight="medium" html-tag="span" class="min-w-0 truncate">
          {{ item.label }}
        </CPText>
      </AppSidebarNavItem>
    </template>
  </AppSidebarNav>
</template>
